import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Stethoscope, Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(email, password);
            navigate("/");
        } catch (err) {
            setError(fmtErr(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F8FAF9] flex items-center justify-center px-4">
            <div className="w-full max-w-md animate-fade-up">
                <div className="mb-8 flex flex-col items-center">
                    <img src="/zen-logo.webp" alt="Zen Hospital" className="h-14 w-auto" data-testid="brand-logo-img" />
                    <p className="mt-2 text-xs text-slate-500">Digital clinic records, simplified</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm" data-testid="doctor-login-card">
                    <h2 className="font-heading text-2xl font-bold text-slate-900">Doctor Sign In</h2>
                    <p className="mt-1 text-sm text-slate-500">Access your practice dashboard</p>
                    <form onSubmit={submit} className="mt-6 space-y-4">
                        <div className="space-y-1.5">
                            <Label htmlFor="email">Email</Label>
                            <Input id="email" data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="doctor@clinic.com" />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="password">Password</Label>
                            <div className="relative">
                                <Input id="password" data-testid="login-password-input" type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
                                <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>
                        {error && <p data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
                        <Button data-testid="login-submit-btn" type="submit" disabled={loading} className="w-full bg-[#E06D1F] hover:bg-[#B45309] text-white rounded-lg py-2.5">
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign In
                        </Button>
                    </form>
                    <div className="mt-4 flex items-center justify-between text-sm">
                        <Link to="/forgot-password" data-testid="forgot-password-link" className="text-[#C2410C] hover:underline font-medium">Forgot password?</Link>
                        <Link to="/register" data-testid="register-link" className="text-slate-600 hover:underline">New doctor? Register</Link>
                    </div>
                </div>
                <p className="mt-6 text-center text-sm text-slate-500">
                    Are you a patient?{" "}
                    <Link to="/portal" data-testid="patient-portal-link" className="font-semibold text-[#E06D1F] hover:underline">Patient Portal</Link>
                </p>
            </div>
        </div>
    );
}
